/**
 * ProfileSocialSheet (web): Gemini-styled alert dialog.
 *
 * Replaces the native bottom-sheet experience with a centered modal that uses
 * the canonical `WebDialog` shell, neutral surfaces, white pill inputs, and
 * sentence-case typography.
 *
 * Each row asks only for the handle - the platform's URL prefix is rendered as
 * fixed text inside the pill (see `ProfileLinkInput`), and a pasted full URL is
 * collapsed back to the handle on commit. The stored value is always the
 * canonical `https://` URL, so the public renderers are unaffected.
 *
 * Company mode (Employer Phase 1, PRD §6.2): the platform list shrinks to the
 * seven launch platforms, and toggling a platform ON requires committing a
 * valid destination first - an enabled icon without a URL is invalid. It also
 * gains a leading row for the company's own website, which is not a social
 * platform and persists to `Company.website` rather than to the socials list -
 * this sheet is where every outbound link on the page is now edited.
 */
import React, { useCallback, useState } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { WebDialog } from '../common/WebDialog.web';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { usePublicProfileBuilderModel } from './PublicProfileBuilderProvider';
import { getPublicProfileRoutes } from '../../lib/public-profile-routes';
import {
  getPublicProfileSocialOrder,
  type PublicProfileSocialId,
} from '../../lib/public-profile-builder';
import {
  getLinkErrorKey,
  getLinkPlaceholder,
  getSocialLinkField,
  getWebsiteLinkField,
} from '../../lib/profile-link-fields';
import { useCompanyWebsiteField } from './useCompanyWebsiteField';
import { ProfileLinkInput } from './ProfileLinkInput.web';
import { ProfileBrandIcon, type ProfileIconId } from './ProfileIconRegistry';

function toBrandIcon(id: PublicProfileSocialId): ProfileIconId {
  if (id === 'tabbio') return 'tabbio';
  if (id === 'email') return 'email';
  if (id === 'whatsapp') return 'whatsapp';
  return id as ProfileIconId;
}

export function ProfileSocialSheet() {
  const router = useRouter();
  const { t } = useTranslation('common');
  const { socials, routeMode, toggleSocial, updateSocialUrl } =
    usePublicProfileBuilderModel();
  const tx = useCallback((key: string) => String(t(key as never)), [t]);

  const items = Array.isArray(socials) ? socials : [];
  const profileRoutes = getPublicProfileRoutes(routeMode);
  const isCompany = routeMode === 'company';
  const website = useCompanyWebsiteField();

  // Rows opened via "Add" but not yet enabled (company validation flow).
  const [pendingIds, setPendingIds] = useState<PublicProfileSocialId[]>([]);

  const closeDialog = useCallback(() => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
      return;
    }
    router.replace(profileRoutes.index as never);
  }, [profileRoutes.index, router]);

  return (
    <WebDialog
      title={tx('publicProfile.socialSheet.title')}
      subtitle={tx('publicProfile.socialSheet.subtitle')}
      onClose={closeDialog}
      maxWidth={560}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {isCompany ? (
          <WebsiteRow
            value={website.value}
            saving={website.isSaving}
            tx={tx}
            onCommit={website.commit}
          />
        ) : null}

        {getPublicProfileSocialOrder(routeMode).map((id) => {
          const social = items.find((item) => item.id === id);
          const enabled = social?.enabled ?? false;
          const value = social?.url ?? '';
          const pending = isCompany && !enabled && pendingIds.includes(id);
          const field = getSocialLinkField(id, routeMode);

          return (
            <SocialRow
              key={id}
              id={id}
              field={field}
              enabled={enabled}
              pending={pending}
              value={value}
              labelKey={`publicProfile.socials.${id}.label`}
              descKey={`publicProfile.socials.${id}.desc`}
              tx={tx}
              addLabel={tx('actions.add')}
              removeLabel={tx('actions.remove')}
              onToggle={() => {
                if (enabled) {
                  toggleSocial(id);
                  setPendingIds((previous) =>
                    previous.filter((item) => item !== id),
                  );
                  return;
                }
                if (isCompany) {
                  setPendingIds((previous) =>
                    previous.includes(id)
                      ? previous.filter((item) => item !== id)
                      : [...previous, id],
                  );
                  return;
                }
                toggleSocial(id);
              }}
              onCommit={(next) => {
                updateSocialUrl(id, next);
                // A committed destination is what enables a company row; an
                // empty commit (the user cleared the field) must not.
                if (next && !enabled) {
                  toggleSocial(id);
                }
                if (next) {
                  setPendingIds((previous) =>
                    previous.filter((item) => item !== id),
                  );
                }
              }}
            />
          );
        })}
      </div>
    </WebDialog>
  );
}

interface SocialRowProps {
  id: PublicProfileSocialId;
  field: ReturnType<typeof getSocialLinkField>;
  enabled: boolean;
  pending: boolean;
  value: string;
  labelKey: string;
  descKey: string;
  tx: (key: string) => string;
  addLabel: string;
  removeLabel: string;
  onToggle: () => void;
  onCommit: (next: string) => void;
}

function SocialRow({
  id,
  field,
  enabled,
  pending,
  value,
  labelKey,
  descKey,
  tx,
  addLabel,
  removeLabel,
  onToggle,
  onCommit,
}: SocialRowProps) {
  const [hover, setHover] = useState(false);
  const showInput = enabled || pending;
  const removable = enabled || pending;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 4px',
        borderBottom: '1px solid var(--gem-outline-variant)',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: 20,
          background: 'rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-primary)',
        }}
      >
        <ProfileBrandIcon id={toBrandIcon(id)} size={18} color="currentColor" />
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 500,
            lineHeight: '20px',
            color: 'var(--text-primary)',
            fontFamily:
              "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          }}
        >
          {tx(labelKey)}
        </div>
        {showInput ? (
          <ProfileLinkInput
            field={field}
            value={value}
            placeholder={getLinkPlaceholder(field, tx)}
            errorText={tx(getLinkErrorKey(field, id))}
            onCommit={onCommit}
          />
        ) : (
          <div
            style={{
              fontSize: 13,
              lineHeight: '18px',
              color: 'var(--text-tertiary)',
              fontFamily:
                "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
            }}
          >
            {tx(descKey)}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onToggle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          flexShrink: 0,
          height: 32,
          minWidth: 64,
          padding: '0 12px',
          borderRadius: 9999,
          border: 'none',
          background: hover ? 'rgba(0,0,0,0.06)' : 'transparent',
          color: removable ? '#d93025' : 'var(--text-primary)',
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: 0,
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          cursor: 'pointer',
          transition: 'background-color 140ms',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        <MaterialSymbol name={removable ? 'close' : 'add'} size={16} />
        {removable ? removeLabel : addLabel}
      </button>
    </div>
  );
}

/**
 * The company website row.
 *
 * It sits above the platform rows and never toggles: a page either has a
 * website or it does not, and clearing the field is how you remove it — there
 * is no separate enabled flag to keep in sync the way the socials have.
 */
function WebsiteRow({
  value,
  saving,
  tx,
  onCommit,
}: {
  value: string;
  saving: boolean;
  tx: (key: string) => string;
  onCommit: (next: string) => void;
}) {
  const field = getWebsiteLinkField();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 4px',
        borderBottom: '1px solid var(--gem-outline-variant)',
        opacity: saving ? 0.6 : 1,
        transition: 'opacity 140ms',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: 20,
          background: 'rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-primary)',
        }}
      >
        <MaterialSymbol name="language" size={20} />
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 500,
            lineHeight: '20px',
            color: 'var(--text-primary)',
            fontFamily:
              "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          }}
        >
          {tx('publicProfile.socials.website.label')}
        </div>
        <ProfileLinkInput
          field={field}
          value={value}
          placeholder={getLinkPlaceholder(field, tx)}
          errorText={tx('publicProfile.linkErrors.url')}
          onCommit={onCommit}
        />
      </div>
    </div>
  );
}
